// Drop it here and VERA files it: any number of documents and photos (quotes, invoices, work orders, manuals,
// inventory lists, notes). One at a time, VERA reads each one, finds the customer, boat or job it belongs to, adds or
// updates those records with the person's own rights, keeps reference material in team knowledge, and says in a few
// lines what she did and what needs the person. Every change is in Activity and can be undone.

import { useRef, useState, type DragEvent } from "react";
import { vera, type Attachment } from "../api/vera";
import { MAX_FILES, readFiles } from "../lib/attachments";
import { CRM_CHANGED } from "../lib/crm";
import { Icon, useStudio } from "./ui";
import { T, Tf } from "../lib/i18n";

type Item = { id: number; name: string; file?: Attachment; state: "waiting" | "reading" | "filed" | "failed"; said?: string };

const FILE_IT = [
  "File this in our records. Read all of it first.",
  "Work out what it is (a quote, invoice, work order, manual, inventory or parts list, notes, a photo…) and who or what it belongs to (a customer, boat, job, supplier).",
  "Find those records with crm_find. Add what's new and update what changed with crm_add and crm_update: contacts, boats, jobs, parts and inventory, prices, dates. Never add the same record twice.",
  "If it's reference material worth keeping (a manual, a price list, a contract), save it to team knowledge too.",
  "Don't ask me questions now: when something isn't clear, leave it and list it under Needs you.",
  "Answer in 3 to 6 short lines: what it was, what you filed where, and anything that needs me.",
].join("\n");

let next = 1;

export function DocumentsInbox() {
  const { getToken, profile, toast } = useStudio();
  const [items, setItems] = useState<Item[]>([]);
  const [over, setOver] = useState(false);
  const running = useRef(false);
  const pick = useRef<HTMLInputElement>(null);
  const queue = useRef<Item[]>([]);

  const patch = (id: number, p: Partial<Item>) => setItems((xs) => xs.map((x) => (x.id === id ? { ...x, ...p } : x)));

  async function work() {
    if (running.current) return;
    running.current = true;
    try {
      for (let item = queue.current.shift(); item; item = queue.current.shift()) {
        patch(item.id, { state: "reading" });
        try {
          const r = await vera.ask({ text: FILE_IT, scope: profile.scopes[0] ?? "marine", attachments: [item.file!], clientTools: ["documents"] }, getToken);
          patch(item.id, { state: "filed", said: r.text, file: undefined });
          window.dispatchEvent(new Event(CRM_CHANGED));
        } catch (e) {
          patch(item.id, { state: "failed", said: e instanceof Error ? e.message : T("VERA couldn't read it. Try it again."), file: undefined });
        }
      }
    } finally { running.current = false; }
  }

  async function add(list: FileList | File[]) {
    const all = Array.from(list);
    const added: Item[] = [];
    for (let i = 0; i < all.length; i += MAX_FILES) {
      const { files, skipped } = await readFiles(all.slice(i, i + MAX_FILES));
      if (skipped.length) toast(Tf("Not added: {list}", { list: skipped.join(", ") }));
      added.push(...files.map((f) => ({ id: next++, name: f.name, file: f, state: "waiting" as const })));
    }
    if (!added.length) return;
    setItems((xs) => [...added, ...xs]);
    queue.current.push(...added);
    void work();
  }

  const waiting = items.filter((x) => x.state === "waiting" || x.state === "reading").length;

  return (
    <section className={`card stack inbox${over ? " over" : ""}`}
      onDragOver={(e: DragEvent) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)}
      onDrop={(e: DragEvent) => { e.preventDefault(); setOver(false); void add(e.dataTransfer.files); }}>
      <div className="row between">
        <h2>{T("Drop it here, VERA files it")}</h2>
        <button className="btn sm" onClick={() => pick.current?.click()}><Icon name="plus" size={14} />{T("Add files")}</button>
      </div>
      <input ref={pick} type="file" multiple hidden onChange={(e) => { if (e.target.files) void add(e.target.files); e.target.value = ""; }} />
      <p className="mute">{T("Quotes, invoices, work orders, manuals, inventory lists, photos: as many as you like. VERA reads each one, puts it with the right customer, boat or job, and tells you what she did. Every change is in Activity and can be undone.")}</p>
      {waiting > 0 && <p className="faint small" role="status">{Tf("VERA is working through {n}. Keep Studio open until she's done.", { n: String(waiting) })}</p>}
      {items.length > 0 && (
        <div className="list">{items.map((x) => (
          <div className="li" key={x.id}>
            <Icon name={x.state === "filed" ? "check" : x.state === "failed" ? "x" : "doc"} size={15} />
            <div className="grow">
              <b>{x.name}</b>
              <div className={`small ${x.state === "failed" ? "err" : "faint"}`} style={{ whiteSpace: "pre-wrap" }}>
                {x.state === "waiting" ? T("Waiting…") : x.state === "reading" ? T("VERA is reading and filing it…") : x.said}
              </div>
            </div>
          </div>))}
        </div>)}
    </section>
  );
}
